import React, { useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useState } from 'react';

import { Popup } from 'devextreme-react/popup';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Button } from 'devextreme-react/button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import Box, { Item as BoxItem } from 'devextreme-react/box';
import AntLocalization from '../utils/AntLocalization';

export default forwardRef(function (props, ref) {
    const onClosedCallback = useRef();
    const [visible, setVisible] = useState(false);
    const [opts, setOpts] = useState({
        title: { vi: 'Thông báo', en: 'Message', fr: 'Message', es: 'Mensaje', id: 'Pesan', ja: 'メッセージ', zh: '提示', ko: '알림', hi: 'संदेश', ar: 'رسالة' },
        content: '',
        cancelText: '',
        cancelIcon: ''
    });
    const [buttons, setButtons] = useState([]);

    const show = useCallback((args, onClosed) => {
        const { title, content, buttons: argsButtons, cancelText, cancelIcon } = args;

        const btns = Array.isArray(props.buttons) ? [...props.buttons] : [];
        if (Array.isArray(argsButtons)) btns.push(...argsButtons);

        const newBtns = btns.map((button, i) => ({
            ...button,
            index: i,
            is_processing: false
        }));

        setButtons(newBtns);
        setOpts({
            title: AntLocalization.translateText(title ?? opts.title),
            content: AntLocalization.translateText(content ?? undefined),
            cancelText: AntLocalization.translateText(cancelText ?? opts.cancelText),
            cancelIcon: cancelIcon ?? opts.cancelIcon
        });
        onClosedCallback.current = onClosed;
        setVisible(true);
    }, [props.buttons, opts.title, opts.cancelText, opts.cancelIcon]);

    const close = useCallback(async (button) => {
        if (onClosedCallback.current) {
            await onClosedCallback.current(button);
        }
        setVisible(false);
    }, []);

    const handleActionClick = useCallback((button) => {
        setButtons(prev => prev.map(b => b.index === button.index ? { ...b, is_processing: true } : b));

        setTimeout(() => {
            close(button);
        }, 200);
    }, [close]);

    useImperativeHandle(ref, () => ({
        show,
        close
    }));

    useEffect(() => {
        if (visible) {
            props.onShowed?.();
        } else {
            props.onClosed?.();
        }
    }, [visible, props.onShowed, props.onClosed]);

    return (
        <Popup
            wrapperAttr={{ class: 'ant-popup' }}
            visible={visible}
            onHiding={() => close(null)}
            dragEnabled={false}
            hideOnOutsideClick={false}
            resizeEnabled={true}
            showTitle={true}
            defaultHeight={props.height ?? 200}
            defaultWidth={props.width ?? 500}
            title={AntLocalization.translateText(props.title ?? opts.title)}
            position="center"
            animation={{
                show: { type: 'fadeIn', duration: 600, from: { scale: 0.55 } },
                hide: { type: 'fadeOut', duration: 600, to: { opacity: 0, scale: 0.55 }, from: { opacity: 1, scale: 1 } }
            }}
        >
            <Box direction="col" height="100%" width="100%">
                <BoxItem ratio={1}>
                    <span
                        style={{
                            marginLeft: props.marginLeft ?? 10,
                            marginRight: props.marginRight ?? 10,
                            marginTop: props.marginTop ?? 10,
                            fontSize: props.fontSize ?? 'larger'
                        }}
                    >
                        {AntLocalization.translateText(props.content ?? opts.content)}
                    </span>
                </BoxItem>
                <BoxItem baseSize={50}>
                    <Toolbar className="ant-bottom-bar">
                        {buttons.map((button) => (
                            <Item key={button.index} location={button.location ?? 'after'}>
                                <Button
                                    onClick={() => handleActionClick(button)}
                                    type={button.type ?? 'normal'}
                                    className="ant-processing-button"
                                    width={button.width ?? 120}
                                >
                                    <LoadIndicator className="ant-indicator-small" visible={button.is_processing} />
                                    <i
                                        className={`dx-icon dx-icon-${button.icon}`}
                                        style={{
                                            marginRight: 9,
                                            display: button.is_processing ? 'none' : 'inline-block'
                                        }}
                                    />
                                    <span className="dx-button-text">
                                        {AntLocalization.translateText(button.is_processing ? button.waiting_text : button.text)}
                                    </span>
                                </Button>
                            </Item>
                        ))}

                        <Item
                            location="after"
                            widget="dxButton"
                            className="ant-processing-button"
                            options={{
                                icon: opts.cancelIcon ?? 'close',
                                text: opts.cancelText || 'OK',
                                type: 'normal',
                                onClick: () => close(null)
                            }}
                        />
                    </Toolbar>
                </BoxItem>
            </Box>
        </Popup>
    );
});
